import React from "react";
import { Flex, LoadingIndicator, Text } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Colors, Sizes } from "@jsq/ds/styles";
import * as styles from "./styles_vanilla.css";
import type { Classification, NodeStatus, TimelineNode } from "#utils/workflowNodes";

/** Tighter than the smallest spacing token (4px) on purpose: the summary and its
 *  classification detail read as one block, not two stacked rows. */
const SUMMARY_DETAIL_GAP = 2;

/** The icons below carry status by glyph and color alone, and FontAwesome marks
 *  an unlabelled icon `aria-hidden`, so the wrapper supplies the name instead. */
const STATUS_LABELS: Record<NodeStatus, string> = {
  PENDING: "Not started",
  RUNNING: "In progress",
  SUCCESS: "Succeeded",
  ERROR: "Failed",
};

type StatusIconProps = {
  status: NodeStatus;
};

/**
 * StatusIcon — spinner while a node is running, checkmark on success, error
 * glyph on failure, so spinners flip to checkmarks one at a time as
 * classifications complete.
 */
const StatusIcon = ({ status }: StatusIconProps) => {
  if (status === "SUCCESS") {
    return <Icon type="circle-check" variant="solid" color={Colors.Success500} />;
  }
  if (status === "ERROR") {
    return <Icon type="exclamation-circle" variant="solid" color={Colors.Danger500} />;
  }
  if (status === "RUNNING") {
    return <LoadingIndicator size="small" variant="primary" />;
  }
  return <Icon type="circle" variant="outline" color={Colors.Neutral500} />;
};

const DOC_TYPE_LABELS: Record<string, string> = {
  pcap: "PCAP",
  financial_statement: "Financial statement",
  workbook: "Supporting workbook",
};

type ClassificationDetailProps = {
  classification: Classification;
};

/**
 * ClassificationDetail — the proposed result for a single document, shown under
 * its "Classifying X" node. Render-only; the confirm / override controls (the
 * HITL ConfirmClassificationCard) are driven by a node payload +
 * submitNodeResponse instead.
 */
const ClassificationDetail = ({ classification: c }: ClassificationDetailProps) => {
  if (c.state === "PENDING") {
    return (
      <Text variant="secondary-sm" asSpan>
        AI detecting…
      </Text>
    );
  }
  if (c.state === "FAILED") {
    return (
      <Text variant="secondary-sm" asSpan>
        Classification failed{c.failureReason ? ` · ${c.failureReason}` : ""}
      </Text>
    );
  }
  const docType = c.documentType
    ? (DOC_TYPE_LABELS[c.documentType] ?? c.documentType)
    : "Unknown type";
  const parts = [docType];
  if (c.confidence) {
    parts.push(`${c.confidence.toLowerCase()} confidence`);
  }
  parts.push(c.entityName ? `Fund: ${c.entityName}` : "Fund: not matched");
  if (c.reportingDate) {
    parts.push(`Period: ${c.reportingDate}`);
  }
  return (
    <Text variant="secondary-sm" asSpan>
      {parts.join(" · ")}
    </Text>
  );
};

export type ClassificationRowProps = {
  node: TimelineNode;
};

/**
 * ClassificationRow — one document in the ingestion slideout: status icon +
 * "Classifying <file>" summary + the proposed classification detail. This is the
 * unit the confirm / override (HITL) controls extend, so it lives in its own
 * module with the status/detail presentation co-located.
 */
export const ClassificationRow = ({ node }: ClassificationRowProps) => (
  <Flex alignItems="flex-start" gap={Sizes.SM}>
    <div className={styles.nodeStatusIcon} role="img" aria-label={STATUS_LABELS[node.status]}>
      <StatusIcon status={node.status} />
    </div>
    <Flex flexDirection="column" gap={SUMMARY_DETAIL_GAP}>
      <Text variant="regular-md" asSpan>
        {node.summary}
      </Text>
      {node.classification && <ClassificationDetail classification={node.classification} />}
    </Flex>
  </Flex>
);
